import React, { useState, useEffect, useContext } from 'react';
import { getPropertyTypeLabel } from '../utils/propertyUtils';
import { AuthContext } from '../contexts/AuthContext';
import { formatDate } from '../utils/dateUtils';
import ExpensesTab from './ExpensesTab';
import ExpenseModal from './ExpenseModal';
import PropertyPhotosTab from './PropertyPhotosTab';
import { buildApiUrl } from '../config/environment';

const PropertyOverview = ({ property, agencyCurrency, onPhotoChange }) => {
  const { user, agencyCountry, agencyId } = useContext(AuthContext);
  const [detailsTab, setDetailsTab] = useState('overview');

  // Helper function for consistent date formatting
  const formatDateWithContext = (dateString) => {
    return formatDate(dateString, {
      userPreference: user?.dateFormatPreference,
      agencyCountry: agencyCountry
    });
  };

  // Expenses state - now self-contained
  const [expenses, setExpenses] = useState([]);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [expenseError, setExpenseError] = useState(null);

  // Fetch expenses from API
  const loadExpenses = async () => {
    if (!property || !property.id) return;

    try {
      setLoadingExpenses(true);
      setExpenseError(null);

      const token = sessionStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(buildApiUrl(`/properties/${property.id}/expenses`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to fetch expenses' }));
        throw new Error(error.message || 'Failed to fetch expenses');
      }

      const data = await response.json();
      // Handle paginated response
      setExpenses(data.expenses || data || []);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      setExpenseError(error.message);
      setExpenses([]);
    } finally {
      setLoadingExpenses(false);
    }
  };

  useEffect(() => {
    loadExpenses();
  }, [property]);

  // Expense handlers - self-contained
  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setShowExpenseModal(true);
  };

  const handleCreateExpense = () => {
    setEditingExpense(null);
    setShowExpenseModal(true);
  };

  const handleSaveExpense = async (expense) => {
    if (!property) return;

    try {
      if (editingExpense) {
        await updateExpense(expense.id, expense);
      } else {
        await createExpense(expense);
      }
    } catch (error) {
      console.error('Error saving expense:', error);
    } finally {
      setEditingExpense(null);
      loadExpenses();
    }
  };

  // Create expense
  const createExpense = async (expenseData) => {
    if (!property || !property.id) return;

    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(buildApiUrl(`/properties/${property.id}/expenses`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(expenseData)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to create expense' }));
        throw new Error(error.message || 'Failed to create expense');
      }

      const newExpense = await response.json();
      setExpenses(prev => [...prev, newExpense]);
      return newExpense;
    } catch (error) {
      console.error('Error creating expense:', error);
      throw error;
    }
  };

  // Update expense
  const updateExpense = async (expenseId, expenseData) => {
    if (!property || !property.id) return;

    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(buildApiUrl(`/properties/${property.id}/expenses/${expenseId}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(expenseData)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to update expense' }));
        throw new Error(error.message || 'Failed to update expense');
      }

      const updatedExpense = await response.json();
      setExpenses(prev => prev.map(exp => exp.id === expenseId ? updatedExpense : exp));
      return updatedExpense;
    } catch (error) {
      console.error('Error updating expense:', error);
      throw error;
    }
  };

  // Delete expense
  const deleteExpense = async (expenseId) => {
    if (!property || !property.id) return;

    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(buildApiUrl(`/properties/${property.id}/expenses/${expenseId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to delete expense' }));
        throw new Error(error.message || 'Failed to delete expense');
      }

      setExpenses(prev => prev.filter(exp => exp.id !== expenseId));
    } catch (error) {
      console.error('Error deleting expense:', error);
      throw error;
    }
  };

  if (!property) return null;

  return (
    <div>
      {/* Sub-tabs for Property Details */}
      <ul className="nav nav-pills nav-fill mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${detailsTab === 'overview' ? 'active' : ''}`}
            onClick={() => setDetailsTab('overview')}
          >
            <i className="bi bi-info-circle me-2"></i>
            Overview
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${detailsTab === 'photos' ? 'active' : ''}`}
            onClick={() => setDetailsTab('photos')}
          >
            <i className="bi bi-images me-2"></i>
            Photos
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${detailsTab === 'expenses' ? 'active' : ''}`}
            onClick={() => setDetailsTab('expenses')}
          >
            <i className="bi bi-file-earmark-text me-2"></i>
            Expenses
          </button>
        </li>
      </ul>

      {/* Tab Content */}
      <div>
        {detailsTab === 'overview' && (
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <h5 className="card-title">
                <i className="bi bi-info-circle me-2"></i>
                Property Details
              </h5>
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="border p-3 rounded">
                    <h6 className="fw-semibold mb-3">Basic Information</h6>
                    <div className="mb-2">
                      <span className="text-muted">Property Name:</span>
                      <div className="fw-semibold">{property.name}</div>
                    </div>
                    <div className="mb-2">
                      <span className="text-muted">Address:</span>
                      <div className="fw-semibold">{property.address}</div>
                    </div>
                    <div className="mb-2">
                      <span className="text-muted">Property Type:</span>
                      <div className="fw-semibold">{getPropertyTypeLabel(property.type)}</div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="border p-3 rounded">
                    <h6 className="fw-semibold mb-3">Additional Information</h6>
                    <div className="mb-2">
                      <span className="text-muted">Notes:</span>
                      <div className="fw-semibold">{property.notes || 'N/A'}</div>
                    </div>
                    <div className="mb-2">
                      <span className="text-muted">Created At:</span>
                      <div className="fw-semibold">{formatDateWithContext(property.createdAt)}</div>
                    </div>
                    <div className="mb-2">
                      <span className="text-muted">Updated At:</span>
                      <div className="fw-semibold">{formatDateWithContext(property.updatedAt)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {detailsTab === 'photos' && (
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <PropertyPhotosTab
                property={property}
                agencyId={agencyId}
                onPhotoChange={onPhotoChange}
              />
            </div>
          </div>
        )}
        {detailsTab === 'expenses' && (
          <ExpensesTab
            expenses={expenses}
            agencyCurrency={agencyCurrency}
            onExpenseUpdate={handleSaveExpense}
            onCreateExpense={handleCreateExpense}
            loading={loadingExpenses}
            error={expenseError}
          />
        )}
      </div>

      {/* Expense Modal - Self-contained */}
      <ExpenseModal
        show={showExpenseModal}
        onHide={() => setShowExpenseModal(false)}
        expense={editingExpense}
        onSave={handleSaveExpense}
        propertyId={property.id}
      />
    </div>
  );
};

export default PropertyOverview;
